import { useEffect } from 'react';

/**
 * The design's motion layer, ported from its component script:
 *  - parallax: every [data-float] element drifts with scroll and tilts toward the
 *    cursor while the pointer is inside its own <section>;
 *  - tilt: [data-tilt] cards rotate in 3D under the cursor;
 *  - deal: the "How it works" cards are dealt in when they scroll into view.
 * All of it is DOM-driven (no React state) so it costs nothing on re-render.
 */
export function useLandingMotion(rootRef: React.RefObject<HTMLElement>, deps: unknown[] = []) {
    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        type FloatEl = HTMLElement & { _hvSec?: HTMLElement | null; _hvCx?: number; _hvCy?: number };
        let els: FloatEl[] = [];
        let px: number | null = null;
        let py: number | null = null;

        const collect = () => {
            els = Array.from(root.querySelectorAll<FloatEl>('[data-float]'));
            for (const el of els) el._hvSec = el.closest('section') || el.parentElement;
        };
        const onMove = (e: MouseEvent) => { px = e.clientX; py = e.clientY; };
        const onLeave = () => { px = null; py = null; };
        window.addEventListener('mousemove', onMove, { passive: true });
        document.addEventListener('mouseleave', onLeave);

        // ---- deal-in for .hv-deal ----
        let dealCheck: (() => void) | null = null;
        let dealt = false;
        let dealTimer: ReturnType<typeof setTimeout> | undefined;
        let onDealScroll: (() => void) | null = null;
        const armDeal = () => {
            const grid = root.querySelector<HTMLElement>('.hv-deal');
            if (!grid || dealt) return;
            if (reduced) { dealt = true; return; }
            const fire = () => {
                if (dealt) return;
                dealt = true;
                grid.classList.add('hv-dealt');
                dealCheck = null;
                if (onDealScroll) window.removeEventListener('scroll', onDealScroll, true);
                dealTimer = setTimeout(() => grid.classList.remove('hv-deal-armed', 'hv-dealt'), 1700);
            };
            const check = () => {
                const r = grid.getBoundingClientRect();
                if (r.top < window.innerHeight * 0.85 && r.bottom > 0) fire();
            };
            const r0 = grid.getBoundingClientRect();
            if (r0.top < window.innerHeight * 0.85 && r0.bottom > 0) { dealt = true; return; } // already on screen: leave visible
            grid.classList.add('hv-deal-armed');
            dealCheck = check;
            onDealScroll = check;
            window.addEventListener('scroll', onDealScroll, { passive: true, capture: true });
        };

        // ---- 3D tilt for [data-tilt] ----
        type TiltEl = HTMLElement & { _hvTilt?: boolean };
        const tiltCleanups: Array<() => void> = [];
        const armTilt = () => {
            if (reduced) return;
            for (const card of Array.from(root.querySelectorAll<TiltEl>('[data-tilt]'))) {
                if (card._hvTilt) continue;
                card._hvTilt = true;
                const layers = Array.from(card.querySelectorAll<HTMLElement>('[data-depth-layer]'));
                const move = (e: MouseEvent) => {
                    const r = card.getBoundingClientRect();
                    const x = (e.clientX - r.left) / r.width - 0.5;
                    const y = (e.clientY - r.top) / r.height - 0.5;
                    card.style.transition = 'transform 0.12s linear, box-shadow 0.3s ease';
                    card.style.transform = `perspective(1100px) rotateY(${(x * 9).toFixed(2)}deg) rotateX(${(-y * 9).toFixed(2)}deg) translateY(-8px) scale(1.015)`;
                    card.style.boxShadow = '0 44px 70px -38px var(--hv-shadow)';
                    for (const l of layers) {
                        const d = parseFloat(l.dataset.depthLayer || '20');
                        l.style.transition = 'transform 0.12s linear';
                        l.style.transform = `translateZ(${d}px)`;
                    }
                };
                const leave = () => {
                    card.style.transition = 'transform 0.5s cubic-bezier(0.2,0.9,0.24,1), box-shadow 0.5s ease';
                    card.style.transform = '';
                    card.style.boxShadow = '';
                    for (const l of layers) { l.style.transition = 'transform 0.5s cubic-bezier(0.2,0.9,0.24,1)'; l.style.transform = ''; }
                };
                card.addEventListener('mousemove', move);
                card.addEventListener('mouseleave', leave);
                tiltCleanups.push(() => {
                    card.removeEventListener('mousemove', move);
                    card.removeEventListener('mouseleave', leave);
                    card._hvTilt = false;
                });
            }
        };

        // ---- parallax tick ----
        const k = reduced ? 0 : 1; // "Lively"
        const tick = () => {
            if (dealCheck) dealCheck();
            if (!els.length) return;
            const vh = window.innerHeight || 800;
            for (const el of els) {
                const rot = el.dataset.rot || '0';
                if (!k) { el.style.transform = `rotate(${rot}deg)`; continue; }
                const d = parseFloat(el.dataset.depth || '4');
                const r = el.getBoundingClientRect();
                if (r.bottom < -260 || r.top > vh + 260) continue;

                let tx = 0, ty = 0;
                const sec = el._hvSec || (el._hvSec = el.closest('section') || el.parentElement);
                if (px !== null && py !== null && sec) {
                    const sr = sec.getBoundingClientRect();
                    if (px >= sr.left && px <= sr.right && py >= sr.top && py <= sr.bottom) {
                        tx = ((px - sr.left) / Math.max(1, sr.width) - 0.5) * 2;
                        ty = ((py - sr.top) / Math.max(1, sr.height) - 0.5) * 2;
                    }
                }
                const cx = (el._hvCx = (el._hvCx || 0) + (tx - (el._hvCx || 0)) * 0.055);
                const cy = (el._hvCy = (el._hvCy || 0) + (ty - (el._hvCy || 0)) * 0.055);

                const c = (r.top + r.height / 2 - vh / 2) / vh;
                const x = cx * d * 5.5 * k;
                const y = (cy * d * 3.6 - c * d * 8.5) * k;
                const tilt = Math.max(4, 18 - d * 0.6);
                el.style.transform = `perspective(760px) translate3d(${x.toFixed(2)}px,${y.toFixed(2)}px,${((d - 7) * 16 * k).toFixed(2)}px) rotateY(${(cx * tilt * 1.5 * k).toFixed(2)}deg) rotateX(${(-cy * tilt * 1.15 * k).toFixed(2)}deg) rotate(${rot}deg)`;
            }
        };

        collect();
        armDeal();
        armTilt();
        const collectTimer = setInterval(() => { collect(); armTilt(); }, 1200);
        let raf = requestAnimationFrame(function loop() { tick(); raf = requestAnimationFrame(loop); });

        return () => {
            window.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseleave', onLeave);
            if (onDealScroll) window.removeEventListener('scroll', onDealScroll, true);
            clearInterval(collectTimer);
            clearTimeout(dealTimer);
            cancelAnimationFrame(raf);
            tiltCleanups.forEach((fn) => fn());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
